
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import type { FormData } from '../types';
import type { PEOProvider } from '@/types/peo.types';

/**
 * Helper function to safely handle potentially null array fields
 */
export const safeArrayFromString = (value: string | string[] | null): string[] => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return [];
  return value.split(',').map(s => s.trim()).filter(Boolean);
};

/**
 * Helper function to generate employee range string from min/max values
 */
const generateEmployeeRange = (min: number | null, max: number | null): string => {
  if (min === null && max === null) return 'Any';
  if (min !== null && max === null) return `${min}+`;
  if (min === null && max !== null) return `0-${max}`;
  return `${min}-${max}`;
};

/**
 * Helper function to format renewal date array for database submission
 * Handles both string arrays and single strings
 */
const formatRenewalDate = (renewalDate: string | string[] | null): string[] | null => {
  if (!renewalDate) return null;
  
  // If it's already an array, return it as is
  if (Array.isArray(renewalDate)) {
    return renewalDate;
  }
  
  // If it's a single string, convert to array
  if (typeof renewalDate === 'string') {
    return [renewalDate];
  }
  
  return null;
};

/**
 * Hook to handle form submission logic for PEO providers
 */
export const useProviderFormSubmit = (
  provider?: PEOProvider,
  onSuccess?: () => void
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleFormSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      console.log('Form data before formatting:', data);
      
      // Generate employee range string from min/max values
      const employeeRange = generateEmployeeRange(data.min_employee_count, data.max_employee_count);
      
      // Format renewal date array for database submission
      const formattedRenewalDate = formatRenewalDate(data.renewal_date);
      
      // Ensure all array fields are properly formatted
      const formattedData = {
        ...data,
        specialties: safeArrayFromString(data.specialties),
        states_served: safeArrayFromString(data.states_served),
        industries_served: safeArrayFromString(data.industries_served || []),
        medical_carriers: safeArrayFromString(data.medical_carriers || []),
        workers_comp_carriers: safeArrayFromString(data.workers_comp_carriers || []),
        physical_offices: safeArrayFromString(data.physical_offices || []),
        ancillary_carriers: safeArrayFromString(data.ancillary_carriers || []),
        renewal_date: formattedRenewalDate,
        employee_range: employeeRange,
        referral_code: data.referral_code?.trim() || null,
        // Enhanced fields with proper array formatting
        excluded_industries_strict: safeArrayFromString(data.excluded_industries_strict || []),
        geographic_focus_states: safeArrayFromString(data.geographic_focus_states || []),
        underwriting_requirements: safeArrayFromString(data.underwriting_requirements || []),
        api_capabilities: safeArrayFromString(data.api_capabilities || []),
        integration_partnerships: safeArrayFromString(data.integration_partnerships || []),
        mobile_app_features: safeArrayFromString(data.mobile_app_features || []),
        key_differentiators: safeArrayFromString(data.key_differentiators || []),
        primary_competitors: safeArrayFromString(data.primary_competitors || []),
        iso_certifications: safeArrayFromString(data.iso_certifications || []),
        industry_awards: safeArrayFromString(data.industry_awards || []),
        // Explicitly convert all boolean fields to boolean values
        is_esac_accredited: data.is_esac_accredited === true,
        irs_certified: data.irs_certified === true,
        audited: data.audited === true,
        fully_bonded_insured: data.fully_bonded_insured === true,
        napeo_member: data.napeo_member === true,
        bbb_accredited: data.bbb_accredited === true,
        aicpa_soc: data.aicpa_soc === true,
        mobile_app: data.mobile_app === true,
        // Carve out fields - ensure boolean values
        allows_medical_carveout: data.allows_medical_carveout === true,
        allows_ancillary_carveout: data.allows_ancillary_carveout === true,
        allows_workers_comp_carveout: data.allows_workers_comp_carveout === true,
        allows_epli_carveout: data.allows_epli_carveout === true,
        allows_401k_carveout: data.allows_401k_carveout === true,
        // Enhanced boolean fields
        dedicated_implementation_manager: data.dedicated_implementation_manager === true,
        white_glove_onboarding: data.white_glove_onboarding === true,
        transition_support_included: data.transition_support_included === true,
        data_migration_included: data.data_migration_included === true,
        requires_minimum_revenue: data.requires_minimum_revenue === true,
        handles_high_risk_industries: data.handles_high_risk_industries === true,
        workers_comp_self_insured: data.workers_comp_self_insured === true,
        multi_state_payroll_expertise: data.multi_state_payroll_expertise === true,
        union_experience: data.union_experience === true,
        prevailing_wage_experience: data.prevailing_wage_experience === true,
        international_payroll: data.international_payroll === true,
        executive_benefits_available: data.executive_benefits_available === true,
        custom_benefit_design: data.custom_benefit_design === true,
        wellness_programs_included: data.wellness_programs_included === true,
        custom_reporting_available: data.custom_reporting_available === true,
        real_time_analytics: data.real_time_analytics === true,
        provides_broker_training: data.provides_broker_training === true,
        joint_marketing_available: data.joint_marketing_available === true,
        business_continuity_plan: data.business_continuity_plan === true,
        case_studies_available: data.case_studies_available === true,
        references_provided: data.references_provided === true
      };
      
      console.log('Formatted data for submission:', formattedData);
      
      if (provider) {
        console.log(`Updating PEO provider: ${provider.name} (ID: ${provider.id})`);
        const { data: updatedData, error } = await supabase
          .from('peo_providers')
          .update(formattedData)
          .eq('id', provider.id)
          .select();
          
        console.log('Update response:', { updatedData, error });
          
        if (error) {
          console.error('Error updating PEO provider:', error);
          console.error('Error code:', error.code);
          console.error('Error message:', error.message);
          console.error('Error details:', error.details);
          
          toast({
            title: 'Error',
            description: `Failed to update PEO provider: ${error.message}`,
            variant: 'destructive',
          });
          return false;
        }
        
        console.log('Provider updated successfully, rows affected:', updatedData ? updatedData.length : 0);
        console.log('Updated data from database:', updatedData);
        
        toast({
          title: 'Success',
          description: 'PEO provider updated successfully',
        });
      } else {
        console.log('Creating new PEO provider:', formattedData.name);
        const { data: insertedData, error } = await supabase
          .from('peo_providers')
          .insert(formattedData)
          .select();
          
        console.log('Insert response:', { insertedData, error });
          
        if (error) {
          console.error('Error creating PEO provider:', error);
          toast({
            title: 'Error',
            description: `Failed to add PEO provider: ${error.message}`,
            variant: 'destructive',
          });
          return false;
        }
        
        console.log('Provider created successfully, rows affected:', insertedData ? insertedData.length : 0);
        console.log('Inserted data from database:', insertedData);
        
        toast({
          title: 'Success',
          description: 'PEO provider added successfully',
        });
      }
      
      // Force a complete refetch of the providers data to ensure we have the latest data
      console.log('Invalidating query cache to force data refresh');
      await queryClient.invalidateQueries({ queryKey: ['peo-providers'] });
      
      // Call the onSuccess callback if provided
      if (onSuccess) {
        console.log('Calling onSuccess callback');
        onSuccess();
      }
      
      return true;
    } catch (error: any) {
      console.error('Error saving PEO provider:', error);
      toast({
        title: 'Error',
        description: `Failed to save PEO provider: ${error.message || 'Unknown error'}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    handleFormSubmit,
    isSubmitting
  };
};
